/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { useControls, folder, button, Leva } from 'leva';
import { Float, Sparkles } from '@react-three/drei';

interface VoiceAgentVisualizationProps {
	volumeLevel: number;
	isAssistantSpeaking: boolean;
}

interface VisualizationSettings {
	size: number;
	detail: number;
	color: string;
	roughness: number;
	metalness: number;
	envMapIntensity: number;
	wireframeVisible: boolean;
	wireframeColor: string;
	wireframeThickness: number;
	wireframeEmissive: number;
	wireframeOpacity: number;
	rotationSpeed: number;
	floatSpeed: number;
	rotationIntensity: number;
	floatIntensity: number;
	floatRange: number;
}

export function VoiceAgentVisualization({
	volumeLevel,
	isAssistantSpeaking,
}: VoiceAgentVisualizationProps) {
	const time = React.useRef(0);

	// Load saved settings if they exist
	const savedSettings = React.useMemo(() => {
		const saved = localStorage.getItem('voiceAgentVisualizationSettings');
		if (saved) {
			try {
				return JSON.parse(saved) as VisualizationSettings;
			} catch (e) {
				console.error('Error loading saved settings:', e);
			}
		}
		return null;
	}, []);

	const {
		size: baseSize,
		detail: baseDetail,
		color: baseColor,
		roughness,
		metalness,
		envMapIntensity,
		wireframeVisible,
		wireframeColor,
		wireframeThickness,
		wireframeEmissive,
		wireframeOpacity,
		rotationSpeed,
		floatSpeed,
		rotationIntensity,
		floatIntensity,
		floatRange,
	} = useControls('Hidden Voice Agent Controls', {
		'Voice Agent Visualization': folder(
			{
				geometry: folder({
					size: {
						value: savedSettings?.size ?? 2.6,
						min: 0.1,
						max: 8,
						step: 0.1,
					},
					detail: {
						value: savedSettings?.detail ?? 0,
						min: 0,
						max: 2,
						step: 1,
					},
				}),
				material: folder({
					color: savedSettings?.color ?? '#ec4899',
					roughness: {
						value: savedSettings?.roughness ?? 0.1,
						min: 0,
						max: 1,
						step: 0.01,
					},
					metalness: {
						value: savedSettings?.metalness ?? 0.8,
						min: 0,
						max: 1,
						step: 0.01,
					},
					envMapIntensity: {
						value: savedSettings?.envMapIntensity ?? 1,
						min: 0,
						max: 5,
						step: 0.1,
					},
				}),
				wireframe: folder({
					wireframeVisible: savedSettings?.wireframeVisible ?? true,
					wireframeColor: savedSettings?.wireframeColor ?? '#ffffff',
					wireframeThickness: {
						value: savedSettings?.wireframeThickness ?? 5,
						min: 0.1,
						max: 33,
						step: 0.1,
					},
					wireframeEmissive: {
						value: savedSettings?.wireframeEmissive ?? 22,
						min: 0,
						max: 55,
						step: 1,
					},
					wireframeOpacity: {
						value: savedSettings?.wireframeOpacity ?? 0.3,
						min: 0,
						max: 1,
						step: 0.01,
					},
				}),
				animation: folder({
					rotationSpeed: {
						value: savedSettings?.rotationSpeed ?? 10,
						min: 1,
						max: 33,
						step: 0.5,
					},
					floatSpeed: {
						value: savedSettings?.floatSpeed ?? 4.7,
						min: 0.1,
						max: 10,
						step: 0.1,
					},
					rotationIntensity: {
						value: savedSettings?.rotationIntensity ?? 4.7,
						min: 0,
						max: 10,
						step: 0.1,
					},
					floatIntensity: {
						value: savedSettings?.floatIntensity ?? 2.5,
						min: 0,
						max: 5,
						step: 0.1,
					},
					floatRange: {
						value: savedSettings?.floatRange ?? -0.2,
						min: -1,
						max: 1,
						step: 0.1,
					},
				}),
			},
			{ collapsed: true }
		),
		Actions: folder(
			{
				'Save Settings': button(() => {
					const settings = {
						size: baseSize,
						detail: baseDetail,
						color: baseColor,
						roughness,
						metalness,
						envMapIntensity,
						wireframeVisible,
						wireframeColor,
						wireframeThickness,
						wireframeEmissive,
						wireframeOpacity,
						rotationSpeed,
						floatSpeed,
						rotationIntensity,
						floatIntensity,
						floatRange,
					};
					localStorage.setItem(
						'voiceAgentVisualizationSettings',
						JSON.stringify(settings)
					);
					alert('Settings saved! Check console for details.');
				}),
				'Show Current Settings': button(() => {
					const saved = localStorage.getItem(
						'voiceAgentVisualizationSettings'
					);
					console.log(
						'Current saved settings:',
						saved ? JSON.parse(saved) : 'No settings saved'
					);
				}),
				'Reset to Defaults': button(() => {
					localStorage.removeItem('voiceAgentVisualizationSettings');
					window.location.reload();
				}),
			},
			{ collapsed: true }
		),
	});

	// Calculate dynamic values based on volume level and speaking state
	const dynamicColor = React.useMemo(() => {
		// Different color schemes for assistant vs user
		if (isAssistantSpeaking) {
			// Assistant: pink to purple
			const startColor = new THREE.Color('#ec4899'); // pink
			const endColor = new THREE.Color('#7e22ce'); // purple
			return startColor.lerp(endColor, volumeLevel);
		} else {
			// User: cyan to blue
			const startColor = new THREE.Color('#06b6d4');
			const endColor = new THREE.Color('#1d4ed8');
			return startColor.lerp(endColor, volumeLevel);
		}
	}, [volumeLevel, isAssistantSpeaking]);

	// Calculate final size with volume influence
	const finalSize = React.useMemo(() => {
		// Use the base size from Leva controls and add a small dynamic component
		const volumeInfluence = Math.sqrt(volumeLevel) * 0.5; // Reduced volume influence
		return baseSize * (1 + volumeInfluence);
	}, [baseSize, volumeLevel]);

	useFrame((state, delta) => {
		const speed = typeof rotationSpeed === 'number' ? rotationSpeed : 10;
		time.current += delta * speed;
	});

	return (
		<>
			<Leva hidden={true} />
			<Float
				speed={floatSpeed}
				rotationIntensity={rotationIntensity}
				floatIntensity={floatIntensity}
				position={[0, floatRange, 0]}
			>
				<group rotation-y={time.current}>
					{/* Base mesh */}
					<mesh>
						<icosahedronGeometry args={[finalSize, baseDetail]} />
						<meshStandardMaterial
							color={dynamicColor}
							roughness={
								typeof roughness === 'number' ? roughness : 0.1
							}
							metalness={
								typeof metalness === 'number' ? metalness : 0.8
							}
							envMapIntensity={
								typeof envMapIntensity === 'number'
									? envMapIntensity
									: 1
							}
						/>
					</mesh>

					{/* Wireframe overlay */}
					{wireframeVisible && (
						<mesh scale={1.001}>
							<icosahedronGeometry
								args={[finalSize, baseDetail]}
							/>
							<meshStandardMaterial
								color={new THREE.Color(wireframeColor)}
								wireframe
								transparent
								opacity={
									typeof wireframeOpacity === 'number'
										? wireframeOpacity
										: 0.3
								}
								depthWrite={false}
							/>
						</mesh>
					)}
				</group>
			</Float>
		</>
	);
}
